import { collection, addDoc, Timestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebaseConfig";
import { Capacitor } from "@capacitor/core";

const SESSION_ID = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
const QUEUE_KEY = "analytics_queue";

function getPlatform() {
  try {
    const p = Capacitor.getPlatform();
    if (p === "ios" || p === "android") return p;
  } catch (_) {}
  return "web";
}

// analyticsService.js  — apenas as duas funções que mudam

async function flushQueue() {
  try {
    // Não tenta flush sem auth — Firestore vai barrar de qualquer jeito
    if (!getAuth().currentUser) return;

    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return;
    const queue = JSON.parse(raw);
    if (!Array.isArray(queue) || !queue.length) return;
    const remaining = [];
    for (const item of queue) {
      try {
        await addDoc(collection(db, "ANALYTICS_EVENTS"), item);
      } catch (err) {
        // `permission-denied` não melhora com retentativa: as rules validam o
        // schema (uid da própria conta ou "anonymous", timestamp de até 30
        // dias). Mantido na fila, o item seria reenviado — e negado — a cada
        // evento, para sempre. Só erro transitório (rede) volta para a fila.
        if (err?.code !== "permission-denied") remaining.push(item);
      }
    }
    if (remaining.length) {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(remaining));
    } else {
      localStorage.removeItem(QUEUE_KEY);
    }
  } catch (_) {}
}

async function logEvent(eventName, data = {}) {
  const currentUser = getAuth().currentUser;

  const event = {
    uid: currentUser?.uid || "anonymous",
    event_name: eventName,
    timestamp: Timestamp.now(),
    session_id: SESSION_ID,
    platform: getPlatform(),
    app_version: process.env.REACT_APP_VERSION || "1.0.0",
    data,
  };

  // Sem usuário autenticado → só enfileira, não tenta escrever
  if (!currentUser) {
    try {
      const raw = localStorage.getItem(QUEUE_KEY);
      const queue = raw ? JSON.parse(raw) : [];
      queue.push(event);
      localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    } catch (_) {}
    return;
  }

  // Autenticado → tenta flush da fila pendente e depois grava o evento atual
  await flushQueue();
  try {
    await addDoc(collection(db, "ANALYTICS_EVENTS"), event);
  } catch (err) {
    console.warn("[Analytics] falha ao salvar evento:", err);
    try {
      const raw = localStorage.getItem(QUEUE_KEY);
      const queue = raw ? JSON.parse(raw) : [];
      queue.push(event);
      localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    } catch (_) {}
  }
}


// ─── Engajamento & Retenção ───────────────────────────────────────────────────

export function logAppOpened() {
  return logEvent("app_opened", { hora_do_dia: new Date().getHours() });
}

export function logSessionStart(platform, appVersion) {
  return logEvent("session_start", {
    platform: platform || getPlatform(),
    app_version: appVersion || process.env.REACT_APP_VERSION || "1.0.0",
    hora_do_dia: new Date().getHours(),
  });
}

export function logSessionEnd(durationSeconds, screensVisited) {
  return logEvent("session_end", {
    duracao_total_segundos: durationSeconds,
    telas_visitadas: screensVisited || [],
  });
}

export function logScreenView(screenName, origin) {
  return logEvent("screen_view", { screen: screenName, origem: origin || null });
}

export function logScreenExit(screenName, timeSpentSeconds) {
  return logEvent("screen_exit", {
    screen: screenName,
    tempo_gasto_segundos: timeSpentSeconds,
  });
}

export function logFirstMeaningfulAction(actionType, secondsAfterLogin) {
  return logEvent("first_meaningful_action", {
    acao_tipo: actionType,
    tempo_ate_acao_segundos: secondsAfterLogin,
  });
}

export function logStreakUpdated(currentStreak, maxStreak) {
  return logEvent("streak_updated", {
    streak_atual: currentStreak,
    streak_maxima: maxStreak,
  });
}

// ─── Treino ───────────────────────────────────────────────────────────────────

export function logWorkoutStarted(planType, muscleGroup) {
  return logEvent("workout_started", {
    plano_tipo: planType || null,
    grupo_muscular: muscleGroup || null,
  });
}

export function logWorkoutCompleted(durationMinutes, totalExercises, completedExercises) {
  return logEvent("workout_completed", {
    duracao_minutos: durationMinutes,
    exercicios_total: totalExercises,
    exercicios_concluidos: completedExercises,
  });
}

// ─── Nutrição ─────────────────────────────────────────────────────────────────

export function logFoodScanStarted(originScreen) {
  return logEvent("food_scan_started", { tela_origem: originScreen || null });
}

export function logFoodScanCompleted(calories, protein, carbs, fat, foodName) {
  return logEvent("food_scan_completed", {
    calorias: calories,
    proteina: protein,
    carbo: carbs,
    gordura: fat,
    alimento_nome: foodName,
  });
}

export function logMealLogged(mealType, hour) {
  return logEvent("meal_logged", { tipo_refeicao: mealType, horario: hour });
}

export function logDailyNutritionComplete(totalMealsRegistered) {
  return logEvent("daily_nutrition_complete", {
    total_refeicoes_registradas_no_dia: totalMealsRegistered,
  });
}

export function logMealRegisteredToday(mealCountToday) {
  return logEvent("meal_registered_today", {
    quantidade_refeicoes_dia: mealCountToday,
  });
}

// ─── Análise Corporal ─────────────────────────────────────────────────────────

export function logBodyScanStarted() {
  return logEvent("body_scan_started", {});
}

export function logBodyScanCompleted(newScore, previousScore) {
  return logEvent("body_scan_completed", {
    score_vita_novo: newScore ?? null,
    score_vita_anterior: previousScore ?? null,
    houve_melhora: (newScore ?? 0) > (previousScore ?? 0),
  });
}

export function logBodyScanAbandoned(abandonStep) {
  return logEvent("body_scan_abandoned", { etapa_abandono: abandonStep });
}

export function logBodyScanNegativeResult(newScore, previousScore) {
  return logEvent("body_scan_negative_result", {
    score_vita_novo: newScore,
    score_vita_anterior: previousScore,
    diferenca: (newScore ?? 0) - (previousScore ?? 0),
  });
}

export function logBodyScanImprovementDetected(pointsGained, daysSinceLastScan) {
  return logEvent("body_scan_improvement_detected", {
    pontos_ganhos: pointsGained,
    dias_desde_ultima_avaliacao: daysSinceLastScan,
  });
}

// ─── Gamificação & Score ──────────────────────────────────────────────────────

export function logXpGained(amount, reason, area) {
  return logEvent("xp_gained", { quantidade: amount, motivo: reason, area });
}

export function logCompetitionViewed(gymCode, period) {
  return logEvent("competition_viewed", {
    gym_code: gymCode || null,
    periodo: period || null,
  });
}

export function logCompetitionJoined(gymCode, period) {
  return logEvent("competition_joined", {
    gym_code: gymCode || null,
    periodo: period || null,
  });
}

export function logRankingViewed(rankingType) {
  return logEvent("ranking_viewed", { tipo_ranking: rankingType || null });
}

export function logTopRankAchieved(position, gymCode) {
  return logEvent("top_rank_achieved", { posicao: position, gym_code: gymCode || null });
}

export function logDietScoreUpdated(newScore, previousScore, reason) {
  return logEvent("diet_score_updated", {
    score_novo: newScore,
    score_anterior: previousScore,
    motivo: reason,
  });
}

export function logScoreEvolutionLogged(area, newScore, previousScore, reason, workoutsRecurrent, scansRecurrent) {
  return logEvent("score_evolution_logged", {
    area,
    score_novo: newScore,
    score_anterior: previousScore,
    motivo_evolucao: reason,
    treinos_recorrentes: workoutsRecurrent,
    scans_recorrentes: scansRecurrent,
  });
}

// ─── Gym & B2B ────────────────────────────────────────────────────────────────

export function logGymLinked(gymCode, origin) {
  return logEvent("gym_linked", { gym_code: gymCode, origem: origin });
}

export function logGymUnlinked(gymCode) {
  return logEvent("gym_unlinked", { gym_code: gymCode });
}

export function logPremiumActivated(origin, durationDays) {
  return logEvent("premium_activated", { origem: origin, duracao_dias: durationDays });
}

export function logPremiumExpired(daysUsed, gymCode) {
  return logEvent("premium_expired", { dias_de_uso: daysUsed, gym_code: gymCode || null });
}

export function logQrScanAttempted(result, gymCode) {
  return logEvent("qr_scan_attempted", { resultado: result, gym_code: gymCode || null });
}

// ─── Navegação & UX ───────────────────────────────────────────────────────────

export function logFeatureAbandoned(featureName, abandonStep) {
  return logEvent("feature_abandoned", {
    funcionalidade: featureName,
    etapa_abandono: abandonStep,
  });
}

export function logButtonClick(buttonId, screenName) {
  return logEvent("button_click", { button_id: buttonId, tela: screenName });
}

export function logOnboardingStepCompleted(step, timeSpentSeconds) {
  return logEvent("onboarding_step_completed", {
    etapa: step,
    tempo_gasto_segundos: timeSpentSeconds,
  });
}

export function logOnboardingAbandoned(abandonStep) {
  return logEvent("onboarding_abandoned", { etapa_abandono: abandonStep });
}

// `extra` (online, rota, origem, pilha, url) vem de src/telemetria/errosDoApp.js.
// O painel agrupa por tipo_erro + tela + platform; o resto é para ler o caso.
export function logErrorOccurred(errorType, screenName, context, extra = {}) {
  return logEvent("error_occurred", {
    ...extra,
    tipo_erro: errorType,
    tela: screenName,
    contexto: context,
  });
}

// ─── Aquisição & Funil ────────────────────────────────────────────────────────

export function logRegistrationCompleted(origin) {
  return logEvent("registration_completed", { origem: origin || "organico" });
}

export function logFirstBodyScanCompleted(hoursSinceRegistration) {
  return logEvent("first_body_scan_completed", {
    tempo_desde_cadastro_horas: hoursSinceRegistration,
  });
}

export function logPremiumBannerSeen(originScreen) {
  return logEvent("premium_banner_seen", { tela_origem: originScreen || null });
}

export function logPremiumBannerClicked(originScreen) {
  return logEvent("premium_banner_clicked", { tela_origem: originScreen || null });
}

export function logReferralUsed(referralCode) {
  return logEvent("referral_used", { codigo_referral: referralCode });
}


// ─── Compartilhamento ─────────────────────────────────────────────────────────
//
// O funil de viralidade NÃO ERA MEDIDO. A recompensa por compartilhar existe
// e funciona ponta a ponta (15 XP, 45 quando verificado, 1x por dia), mas não
// havia um só evento `share_*` — dava para saber quantos foram aprovados pela
// fila do painel e nada sobre quantos desistiram no caminho. Os quatro passos
// abaixo são o funil inteiro; a diferença entre eles é onde a perda acontece.

/** A arte ficou pronta na tela (story ou post). */
export function logShareArteGerada(tipo, formato) {
  return logEvent("share_arte_gerada", {
    tipo: tipo || null,
    formato: formato || null,
  });
}

/** A folha do sistema abriu — ainda não quer dizer que compartilhou. */
export function logShareFolhaAberta(tipo, via) {
  return logEvent("share_folha_aberta", { tipo: tipo || null, via: via || null });
}

/**
 * A pessoa declarou que compartilhou (o botão "Eu compartilhei").
 *
 * É o ÚNICO ponto que credita, porque `navigator.share` resolve mesmo quando
 * a folha é cancelada — medir a folha como conversão infla o funil.
 */
export function logShareDeclarado(tipo, canal) {
  return logEvent("share_declarado", { tipo: tipo || null, canal: canal || null });
}

/** A verificação manual aprovou e o bônus foi creditado. */
export function logShareVerificado(canal, multiplicador) {
  return logEvent("share_verificado", {
    canal: canal || null,
    multiplicador: multiplicador || null,
  });
}

// ─── Streak ─────────────────────────────────────────────────────────────────

export function logStreakCheckIn(currentStreak, dayOfWeek, hour) {
  return logEvent("streak_check_in", {
    streak_atual: currentStreak,
    dia_da_semana: dayOfWeek,
    hora: hour,
  });
}

export function logStreakBroken(brokenStreak, daysWithout, dayOfWeekBroken) {
  return logEvent("streak_broken", {
    streak_que_quebrou: brokenStreak,
    dias_sem_checkin: daysWithout,
    dia_da_semana_quebrou: dayOfWeekBroken,
  });
}

export function logStreakRecovered(daysWithout, newStreak, previousStreak) {
  return logEvent("streak_recovered", {
    dias_que_ficou_sem: daysWithout,
    streak_nova: newStreak,
    streak_anterior: previousStreak,
  });
}

export function logStreakWeeklySummary(checkInsWeek, daysWithCheckin, bestDayOfWeek, worstDayOfWeek) {
  return logEvent("streak_weekly_summary", {
    check_ins_semana: checkInsWeek,
    dias_com_checkin: daysWithCheckin,
    melhor_dia_semana: bestDayOfWeek,
    pior_dia_semana: worstDayOfWeek,
  });
}

// ── Ads (criativos próprios LesVita) + rewarded ─────────────────────────────
export function logAdShown(tipo, creativoId, comContador) {
  return logEvent("ad_shown", {
    tipo: tipo || null,
    creativo_id: creativoId || null,
    com_contador: !!comContador,
  });
}

export function logAdCtaTap(tipo, creativoId, origem) {
  return logEvent("ad_cta_tap", {
    tipo: tipo || null,
    creativo_id: creativoId || null,
    origem: origem || null,
  });
}

export function logRewarded(mode, granted) {
  return logEvent("rewarded_granted", {
    modo: mode || null,
    concedido: !!granted,
  });
}

// ─── Compra de tickets (pacote consumível) ───────────────────────────────────
//
// Funil completo da compra do "Pacote de Tickets Extra". Sem isto, uma compra
// que falha na loja não deixa rastro em lugar nenhum: o webhook do RevenueCat
// só é chamado quando a compra dá CERTO, então tudo que acontece antes (loja
// sem o produto, usuário desistindo, erro de billing) era invisível.
//
// `checkout_id` amarra as etapas de UMA tentativa — é o que permite ao painel
// dizer "abriu, tocou em comprar e o Google devolveu erro" em vez de contar
// eventos soltos.

export function logTicketsPaywallViewed(contexto, productId, disponivel) {
  return logEvent("tickets_paywall_viewed", {
    contexto: contexto || null,
    product_id: productId || null,
    produto_disponivel: !!disponivel,
  });
}

export function logTicketsProductUnavailable(contexto, productId, motivo) {
  return logEvent("tickets_product_unavailable", {
    contexto: contexto || null,
    product_id: productId || null,
    motivo: motivo || null,
  });
}

export function logTicketsCheckoutStarted(checkoutId, contexto, productId, preco) {
  return logEvent("tickets_checkout_started", {
    checkout_id: checkoutId || null,
    contexto: contexto || null,
    product_id: productId || null,
    preco: preco || null,
  });
}

export function logTicketsPurchaseCompleted(checkoutId, contexto, productId, transactionId, preco) {
  return logEvent("tickets_purchase_completed", {
    checkout_id: checkoutId || null,
    contexto: contexto || null,
    product_id: productId || null,
    transaction_id: transactionId || null,
    preco: preco || null,
  });
}

export function logTicketsPurchaseCancelled(checkoutId, contexto, productId) {
  return logEvent("tickets_purchase_cancelled", {
    checkout_id: checkoutId || null,
    contexto: contexto || null,
    product_id: productId || null,
  });
}

export function logTicketsPurchaseFailed(checkoutId, contexto, productId, codigo, mensagem) {
  return logEvent("tickets_purchase_failed", {
    checkout_id: checkoutId || null,
    contexto: contexto || null,
    product_id: productId || null,
    // `codigo` é o PURCHASES_ERROR_CODE do RevenueCat quando existe; é o que
    // separa "cartão recusado" de "produto não configurado na loja".
    codigo: codigo != null ? String(codigo) : null,
    mensagem: mensagem ? String(mensagem).slice(0, 300) : null,
  });
}

export function logTicketsRestore(ok, erro) {
  return logEvent("tickets_restore", {
    ok: !!ok,
    erro: erro ? String(erro).slice(0, 300) : null,
  });
}

export { flushQueue as flushAnalyticsQueue };